/**
 * Live-latency tuning for the nginx-rtmp HLS output.
 *
 * `hls_fragment 1` is a REQUEST, NOT A PROMISE. nginx-rtmp can only cut a
 * fragment on a keyframe, so the fragment duration is whatever GOP the
 * publisher happens to push. Measured on the demo box against a live Matrice
 * (2026-08-28), the drone's playlist comes back as:
 *
 *     #EXT-X-TARGETDURATION:2
 *     #EXTINF:1.983 / 2.021 / 1.980
 *
 * i.e. a ~2 s GOP, twice what the server asked for. Every number here is
 * derived from that measured 2 s, never from the 1 s in `rtmp/nginx.conf`.
 */

/** What a keyframe-cut fragment actually comes out at. Measured, not configured. */
export const MEASURED_TARGET_DURATION_S = 2;

/**
 * hls.js: how many target-durations behind the live edge to sit.
 *
 * 2 x the measured 2 s = ~4 s behind live. This was 3 (~6 s) for exactly one
 * hour: it was raised to buy smoothness for a tile that was starving because
 * the aircraft pushed 1280x720 while its eight neighbours pushed 640x360, and
 * `DJI_LIVE_VIDEO_QUALITY` landed straight afterwards and fixed that at the
 * source (`.env.demo` asks for `smooth`). The compensation outlived the fault
 * it was compensating for, and every HLS viewer went on paying the 2 s.
 *
 * Do not take it to 1. A single fragment of headroom is what the starving tile
 * had, and a publisher whose GOP wanders is then permanently at the edge.
 * Whatever this becomes, MAX_BUFFER_LENGTH_S and hls_playlist_length still have
 * to satisfy the two predicates below -- at every GOP, not just the Matrice's.
 */
export const LIVE_SYNC_SEGMENTS = 2;
/** hls.js: how far behind before it gives up and jumps to live (a visible skip). */
export const LIVE_MAX_SEGMENTS = 8;
/** hls.js: the forward buffer cap, in seconds. */
export const MAX_BUFFER_LENGTH_S = 20;

/**
 * Where hls.js aims the playhead: `liveSyncDurationCount * targetduration`.
 * (LatencyController.targetLatency, hls.js 1.6.)
 */
export function targetLatencyS(syncSegments: number, targetDurationS: number): number {
  return syncSegments * targetDurationS;
}

/**
 * hls.js can only hold as much latency as it is allowed to buffer.
 *
 * The playhead sits `targetLatency` behind the live edge, so everything between
 * the playhead and the edge has to fit in `maxBufferLength`. Cap the buffer
 * below that and the forward buffer reaches the live edge and stops growing —
 * the player runs on a near-empty buffer and starves on the first stall.
 * One extra fragment of headroom, because a fragment arrives all at once.
 */
export function bufferCoversLatency(
  maxBufferLengthS: number, syncSegments: number, targetDurationS: number,
): boolean {
  return maxBufferLengthS >= targetLatencyS(syncSegments, targetDurationS) + targetDurationS;
}

/**
 * The server half of the same budget: `hls_playlist_length` in rtmp/nginx.conf.
 *
 * `hls_cleanup on` deletes a segment the moment it rolls off the playlist. Sit
 * `targetLatency` behind the edge of a playlist barely longer than that and the
 * segment being decoded is deleted underneath the player — a 404, which is a
 * fatal NETWORK_ERROR and a full hls.js teardown/rebuild, not a dropped frame.
 * Two fragments of margin.
 */
export function playlistCoversLatency(
  playlistLengthS: number, syncSegments: number, targetDurationS: number,
): boolean {
  return playlistLengthS >= targetLatencyS(syncSegments, targetDurationS) + 2 * targetDurationS;
}

/** The hls.js config every HLS feed is played with. */
export const HLS_LIVE_CONFIG = {
  enableWorker: true,
  /* nginx-rtmp emits plain HLS — no EXT-X-PART, no PART-HOLD-BACK — so the
     low-latency machinery has nothing to act on and only makes hls.js more
     aggressive about chasing an edge it cannot resolve any finer. */
  lowLatencyMode: false,
  liveSyncDurationCount: LIVE_SYNC_SEGMENTS,
  liveMaxLatencyDurationCount: LIVE_MAX_SEGMENTS,
  maxBufferLength: MAX_BUFFER_LENGTH_S,
  backBufferLength: 0,
  manifestLoadingMaxRetry: 6,
  manifestLoadingRetryDelay: 2000,
  levelLoadingMaxRetry: 6,
  levelLoadingRetryDelay: 2000,
} as const;
